import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import {
  Row,
  Col,
  Image,
  ListGroup,
  Button,
  Card,
  FormControl,
  Form,
  Modal,
  Toast,
  ToastContainer,
} from "react-bootstrap";
import { listProductDetails } from "../redux/actions/productActions";
import Loading from "../components/Loading";
import Message from "../components/Message";
import Rating from "../components/Rating";
import { addToCart } from "../redux/actions/cartActions";
import { createProductReview } from "../redux/actions/productActions";
import { Carousel } from "react-bootstrap";
import { listCategoryDiscounts } from "../redux/actions/categoryDiscountActions";
import SizeChart from "../components/SizeChart";
import { setDirectPurchase } from "../redux/actions/cartActions";
import { useGlobalLoading } from "../context/GlobalLoadingContext";

const ProductScreen = () => {
  const [qty, setQty] = useState(1);
  const [selectedSize, setSelectedSize] = useState("");
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [showReturnPolicy, setShowReturnPolicy] = useState(false);

  // New states for floating panel control and responsiveness
  const [showAddToCartPanel, setShowAddToCartPanel] = useState(false);
  const [showBuyNowPanel, setShowBuyNowPanel] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  // New states for image loading
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [imagesToLoad, setImagesToLoad] = useState(0);
  const [showCartToast, setShowCartToast] = useState(false);

  let navigate = useNavigate();
  const dispatch = useDispatch();
  let params = useParams();
  const location = useLocation();

  // Back navigation support: check if user came from PlaceOrderScreen or Cart
  const fromPlaceOrder = location.state?.from === "/placeorder";
  const fromCart = location.state?.from === "/cart";

  const productDetails = useSelector((state) => state.productDetails);
  const { product, loading, error } = productDetails;

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const categoryDiscountList = useSelector((state) => state.categoryDiscountList);
  const { discounts = [] } = categoryDiscountList;

  const productReviewCreate = useSelector((state) => state.productReviewCreate);
  const {
    loading: loadingProductReview,
    success: successProductReview,
    error: errorProductReview,
  } = productReviewCreate;

  const { startLoading, stopLoading } = useGlobalLoading();

  useEffect(() => {
    if (successProductReview) {
      setRating(0);
      setComment("");
    }
    if (errorProductReview) {
      setRating(0);
      setComment("");
    }
    
    if (error) {
      navigate("/");
    }
    dispatch(listProductDetails(params.id));
    dispatch(listCategoryDiscounts());
    setImagesLoaded(false);
    setImagesToLoad(0);
  }, [dispatch, params.id, successProductReview, errorProductReview]);

  // Decide how many images this product has (carousel vs single image)
  useEffect(() => {
    if (!product) return;

    if (product.carouselImages && product.carouselImages.length > 0) {
      setImagesToLoad(product.carouselImages.length);
    } else {
      setImagesToLoad(1);
    }
    setImagesLoaded(false);
  }, [product]);

  // Handle viewport width for mobile / tablet logic
  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 992);
    }
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Image onLoad handler to track when all required images have loaded
  const handleImageLoaded = () => {
    setImagesToLoad((prev) => {
      const next = prev - 1;
      if (next <= 0) {
        setImagesLoaded(true);
      }
      return next;
    });
  };

  // Sync global loading with both API loading and imagesLoaded
  useEffect(() => {
    if (loading) {
      startLoading();
    } else if (!loading && imagesLoaded) {
      stopLoading();
    }
  }, [loading, imagesLoaded, startLoading, stopLoading]);

  const getCategoryDiscount = (category) => {
    const discountObj = discounts.find((d) => d.category === category);
    return discountObj ? discountObj.percent : 0;
  };

  const discountPercent = product ? getCategoryDiscount(product.category) : 0;
  const discountedPrice =
    product && discountPercent
      ? (product.price * (1 - discountPercent / 100)).toFixed(2)
      : product?.price || 0;

  const sizeObj = product?.sizes?.find((sz) => sz.size === selectedSize);

  const addToCartHandler = () => {
    if (!selectedSize) {
      alert("Please select a size");
      return;
    }
    dispatch(addToCart(params.id, qty, selectedSize));
    setShowCartToast(true);
  };

  const submitHandler = (e) => {
    e.preventDefault();
    
    if (!rating || rating === "" || !comment || comment.trim() === "") {
      alert("Please select a rating and provide a comment");
      return;
    }
    
    dispatch(createProductReview(params.id, { rating, comment }));
  };

  const handleBuyNowDirect = () => {
    if (!selectedSize) {
      alert("Please select a size");
      return;
    }
    dispatch(setDirectPurchase(product, qty, selectedSize));
    navigate("/login?redirect=shipping-payment", { state: { from: "buynow" } });
  };

  // NEW: Size button click handler
  const handleSizeSelect = (size) => {
    setSelectedSize(size);
    setQty(1);
  };

  return (
    <>
      {/* Back button to PlaceOrderScreen or Cart if user came from there */}
      {fromPlaceOrder ? (
        <Button
          variant="light"
          onClick={() => navigate(-1)}
          aria-label="Back to Place Order"
          style={{ marginRight: 10 }}
        >
          &larr; Back to Order
        </Button>
      ) : fromCart ? (
        <Button
          variant="light"
          onClick={() => navigate(-1)}
          aria-label="Back to Cart"
          style={{ marginRight: 10 }}
        >
          &larr; Back to Cart
        </Button>
      ) : (
        <Button
          variant="light"
          onClick={() => {
            if (window.history.length > 1) {
              navigate(-1);
            } else {
              navigate("/");
            }
          }}
          aria-label="Go back"
        >
          &larr; Go back
        </Button>
      )}

      {loading ? (
        <Loading />
      ) : error ? (
        <Message variant="danger">{error}</Message>
      ) : (
        product && (
          <>
            <Row>
              <Col xs={12} md={6}>
                {product.carouselImages && product.carouselImages.length > 0 ? (
                  <Carousel>
                    {product.carouselImages.map((img, idx) => (
                      <Carousel.Item key={idx}>
                        <Image
                          src={img}
                          alt={`Slide ${idx}`}
                          fluid
                          style={{
                            padding: "20px",
                            display: "flex",
                            justifySelf: "center",
                          }}
                          onLoad={handleImageLoaded}
                        />
                      </Carousel.Item>
                    ))}
                  </Carousel>
                ) : (
                  <Image
                    src={product.image}
                    alt={product.name}
                    fluid
                    style={{ padding: "20px" }}
                    onLoad={handleImageLoaded}
                  />
                )}
              </Col>
              <Col xs={12} md={6}>
                <h3>{product.name}</h3>
                <ListGroup variant="flush">
                  <ListGroup.Item>
                    <Rating
                      value={product.rating}
                      text={`${product.rating?.toFixed(1) || 0} Reviews`}
                    />
                  </ListGroup.Item>
                  <ListGroup.Item>
                    {discountPercent > 0 ? (
                      <>
                        Price:{" "}
                        <span
                          style={{
                            textDecoration: "line-through",
                            color: "#999",
                            marginRight: 8,
                          }}
                        >
                          ₹{Math.round(product.price)}
                        </span>
                        <span style={{ color: "#198754", fontWeight: 400 }}>
                          ₹{Math.round(discountedPrice)} ({discountPercent}%
                          OFF)
                        </span>
                      </>
                    ) : (
                      <>Price: ₹{Math.round(product.price)}</>
                    )}
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <p style={{ fontSize: "18px", marginBottom: "0px" }}>
                      Description:
                    </p>
                    <p className="mb-0 mt-2">{product.description}</p>
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <span
                      style={{
                        color: "#007bff",
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                      onClick={() => setShowReturnPolicy(true)}
                    >
                      View Return Policy
                    </span>
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <SizeChart />
                  </ListGroup.Item>

                  {/* NEW: Square size buttons for mobile/tablet - horizontally scrollable */}
{(isMobile || isTablet) && (
  <ListGroup.Item>
    <Form.Label className="mb-2">
      Size <span style={{color: 'red'}}>*</span>
    </Form.Label>
    <div 
      className="d-flex flex-nowrap overflow-auto pb-2" 
      style={{ 
        scrollbarWidth: 'none', 
        msOverflowStyle: 'none' 
      }}
    >
      {product.sizes?.map((sz, i) => {
        const isSelected = selectedSize === sz.size;
        const sizeObj = product.sizes.find(s => s.size === sz.size);
        const isOutOfStock = sizeObj?.quantity === 0;
        
        return (
          <div key={i} className="d-flex flex-column" style={{marginRight: "12px", flexShrink: 0}}>
            <Button
              variant="light"
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "6px",
                padding: "0",
                border: isSelected 
                  ? "1px solid #474747" 
                  : `1px solid ${isOutOfStock ? '#ccc' : '#474747'}`,
                backgroundColor: isSelected ? "#474747" : "transparent",
                color: isSelected ? "#ffffff" : (isOutOfStock ? "#999" : "#474747"),
                fontSize: isOutOfStock ? "11px" : "14px",
                fontWeight: "500",
                opacity: isOutOfStock ? 0.5 : 1,
                flexShrink: 0,
              }}
              onClick={() => !isOutOfStock && handleSizeSelect(sz.size)}
              disabled={isOutOfStock}
            >
              {sz.size}
            </Button>
            {isOutOfStock && (
              <small style={{ 
                color: "#dc3545", 
                fontSize: "10px", 
                marginTop: "2px", 
                textAlign: "center",
                width: "48px"
              }}>
                Out of Stock
              </small>
            )}
          </div>
        );
      })}
    </div>
    <style jsx>{`
      .overflow-auto::-webkit-scrollbar {
        display: none;
      }
    `}</style>
  </ListGroup.Item>

                  )}
                </ListGroup>

                {/* Desktop dropdown (unchanged) */}
                {!isMobile && !isTablet && (
                  <Col xs={12} md={12}>
                    <ListGroup variant="flush">
                      <ListGroup.Item>
                        <Form.Group>
                          <Form.Label>Size</Form.Label>
                          <Form.Control
                            as="select"
                            className="custom-dropdown"
                            value={selectedSize}
                            onChange={(e) => {
                              setSelectedSize(e.target.value);
                              setQty(1);
                            }}
                          >
                            <option value="">Choose Size</option>
                            {product.sizes &&
                              product.sizes.map((sz, i) => (
                                <option key={i} value={sz.size}>
                                  {sz.size}
                                </option>
                              ))}
                          </Form.Control>
                        </Form.Group>
                      </ListGroup.Item>
                      {/* Rest of desktop remains same */}
                      {sizeObj && (
                        <ListGroup.Item>
                          <Row>
                            <Col>Qty</Col>
                            <Col>
                              {sizeObj.quantity > 0 ? (
                                <div className="d-flex align-items-center">
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() =>
                                      setQty((prev) => Math.max(prev - 1, 1))
                                    }
                                    disabled={qty <= 1}
                                    className="custom-qty-button"
                                  >
                                    -
                                  </Button>
                                  <FormControl
                                    value={qty}
                                    readOnly
                                    className="mx-2 text-center"
                                    style={{
                                      width: "50px",
                                      userSelect: "none",
                                    }}
                                  />
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() =>
                                      setQty((prev) =>
                                        Math.min(
                                          prev + 1,
                                          Math.min(sizeObj.quantity, 10)
                                        )
                                      )
                                    }
                                    disabled={
                                      qty >= Math.min(sizeObj.quantity, 10)
                                    }
                                    className="custom-qty-button"
                                  >
                                    +
                                  </Button>
                                </div>
                              ) : (
                                <span className="text-danger">
                                  Out of Stock
                                </span>
                              )}
                            </Col>
                          </Row>
                        </ListGroup.Item>
                      )}

                      <ListGroup.Item>
                        <Button
                          onClick={addToCartHandler}
                          className="btn btn-primary d-block w-100 mb-2"
                          type="button"
                          disabled={!sizeObj || sizeObj.quantity === 0}
                        >
                          Add to Cart
                        </Button>
                        <Button
                          onClick={handleBuyNowDirect}
                          className="btn d-block w-100"
                          disabled={!sizeObj || sizeObj.quantity === 0}
                        >
                          Buy Now
                        </Button>
                      </ListGroup.Item>
                    </ListGroup>
                  </Col>
                )}
              </Col>
            </Row>

            {/* Reviews section unchanged */}
            <Row className="mt-4">
              <Col md={6} style={{marginBottom:"40px"}}>
                <h2>Reviews</h2>
                {loadingProductReview && <Loading />}
                {product.reviews.length === 0 && (
                  <Message variant="info">No Reviews</Message>
                )}
                <ListGroup variant="flush">
                  {product.reviews.map((review) => (
                    <ListGroup.Item key={review._id}>
                      <strong>{review.name}</strong>
                      <Rating value={review.rating} />
                      <p style={{ margin: "5px 0px" }}>
                        {review.createdAt.substring(0, 10)}
                      </p>
                      <p style={{ margin: "5px 0px" }}>{review.comment}</p>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
                <h3 style={{ margin: "20px 0px" }}>Write a customer review</h3>
                {userInfo ? (
                  <Form onSubmit={submitHandler}>
                    {errorProductReview && (
                      <Message variant="danger">{errorProductReview}</Message>
                    )}
                    <Form.Group controlId="rating">
                      <Form.Label>Rating</Form.Label>
                      <Form.Control
                        as="select"
                        value={rating}
                        onChange={(e) => setRating(e.target.value)}
                      >
                        <option value="">Select ...</option>
                        <option value="1">1 - Poor</option>
                        <option value="2">2 - Fair</option>
                        <option value="3">3 - Good</option>
                        <option value="4">4 - Very good</option>
                        <option value="5">5 - Perfect</option>
                      </Form.Control>
                    </Form.Group>
                    <Form.Group className="mt-3" controlId="comment">
                      <Form.Label>Comment</Form.Label>
                      <Form.Control
                        as="textarea"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder="Comment"
                      ></Form.Control>
                    </Form.Group>
                    <Button
                      type="submit"
                      className="mt-3"
                      variant="primary"
                      style={{ marginBottom: "40px", borderRadius: "2px" }}
                    >
                      Submit
                    </Button>
                  </Form>
                ) : (
                  <Message variant="info">
                    <Link to="/login">Sign in</Link> to write a review
                  </Message>
                )}
              </Col>
            </Row>
          </>
        )
      )}

      {/* Rest of modals and floating panels EXACTLY SAME as previous version */}
      {/* Return Policy Modal */}
      <Modal
        show={showReturnPolicy}
        onHide={() => setShowReturnPolicy(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Return Policy</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h5 className="mb-3">Return Guidelines</h5>
          <ol>
            <li className="mb-2">
              <strong>Original Tags:</strong> Product tags must be intact and
              not removed or damaged.
            </li>
            <li className="mb-2">
              <strong>Unused Condition:</strong> Items must be unworn, unwashed,
              and in their original condition.
            </li>
            <li className="mb-2">
              <strong>Return Window:</strong> Returns accepted within 7 days of
              delivery.
            </li>
            <li className="mb-2">
              <strong>Original Packaging:</strong> Product must be returned in
              original packaging with all accessories.
            </li>
            <li className="mb-2">
              <strong>No Damage:</strong> Products should not have any stains,
              tears, or alterations.
            </li>
            <li className="mb-2">
              <strong>Return Process:</strong> Email us at{" "}
              <a href="mailto:returns@yourstore.com">returns@yourstore.com</a>{" "}
              with:
              <ul className="mt-2">
                <li>Order number</li>
                <li>Reason for return</li>
                <li>Clear images of the product</li>
              </ul>
            </li>
          </ol>
          <p className="mt-3 text-muted">
            <small>
              * Refunds will be processed within 5-7 business days after
              receiving the returned item.
            </small>
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowReturnPolicy(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Floating Buttons for mobile/tablet - SIMPLIFIED (no size selection here) */}
      {(isMobile || isTablet) && (
        <>
          <div
            style={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: "#ffffffff",
              color: "white",
              display: "flex",
              justifyContent: "space-around",
              padding: "10px 0",
              zIndex: 1050,
            }}
          >
            <Button
              variant="light"
              style={{
                width: "48%",
                color: "#474747",
                backgroundColor: "#ffffffff",
                border: "1px solid #474747",
                borderRadius: "2px",
              }}
              onClick={() => {
                setShowAddToCartPanel((prev) => !prev);
                setShowBuyNowPanel(false);
              }}
              aria-expanded={showAddToCartPanel}
              disabled={!selectedSize}
            >
              Add to Cart
            </Button>
            <Button
              variant="light"
              style={{
                width: "48%",
                color: "#ffffffff",
                backgroundColor: "#474747",
                borderRadius: "2px",
              }}
              onClick={() => {
                setShowBuyNowPanel((prev) => !prev);
                setShowAddToCartPanel(false);
              }}
              aria-expanded={showBuyNowPanel}
              disabled={!selectedSize}
            >
              Buy Now
            </Button>
          </div>

          {/* Add to Cart Floating Panel - QUANTITY ONLY */}
          {showAddToCartPanel && (
            <div
              style={{
                position: "fixed",
                bottom: "56px",
                left: 0,
                right: 0,
                backgroundColor: "white",
                padding: "20px",
                boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
                zIndex: 1051,
              }}
            >
              <Button
                variant="light"
                size="sm"
                style={{
                  position: "absolute",
                  top: "10px",
                  right: "10px",
                  borderRadius: "50%",
                  width: "25px",
                  height: "25px",
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "24px",
                }}
                onClick={() => setShowAddToCartPanel(false)}
              >
                &times;
              </Button>

              {selectedSize && (
                <>
                  <div className="mb-3 p-2 bg-light rounded">
                    <strong>Size: {selectedSize}</strong>
                  </div>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Quantity</Form.Label>
                    <div className="d-flex align-items-center">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setQty((prev) => Math.max(prev - 1, 1))}
                        disabled={qty <= 1}
                      >
                        -
                      </Button>
                      <FormControl
                        value={qty}
                        readOnly
                        className="mx-2 text-center"
                        style={{ width: "50px", userSelect: "none" }}
                      />
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() =>
                          setQty((prev) =>
                            Math.min(
                              prev + 1,
                              Math.min(
                                product.sizes.find(
                                  (sz) => sz.size === selectedSize
                                )?.quantity || 1,
                                10
                              )
                            )
                          )
                        }
                        disabled={
                          qty >=
                          Math.min(
                            product.sizes.find((sz) => sz.size === selectedSize)
                              ?.quantity || 1,
                            10
                          )
                        }
                      >
                        +
                      </Button>
                    </div>
                  </Form.Group>
                </>
              )}

              <Button
                variant="primary"
                className="mt-2 w-100"
                disabled={!selectedSize || !sizeObj || sizeObj.quantity === 0}
                onClick={() => {
                  addToCartHandler();
                  setShowAddToCartPanel(false);
                }}
              >
                Add to Cart
              </Button>
            </div>
          )}

          {/* Buy Now Floating Panel - QUANTITY ONLY */}
          {showBuyNowPanel && (
            <div
              style={{
                position: "fixed",
                bottom: "56px",
                left: 0,
                right: 0,
                backgroundColor: "white",
                padding: "20px",
                boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
                zIndex: 1051,
              }}
            >
              <Button
                variant="light"
                size="sm"
                style={{
                  position: "absolute",
                  top: "10px",
                  right: "10px",
                  borderRadius: "50%",
                  width: "25px",
                  height: "25px",
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "24px",
                }}
                onClick={() => setShowBuyNowPanel(false)}
              >
                &times;
              </Button>

              {selectedSize && (
                <>
                  <div className="mb-3 p-2 bg-light rounded">
                    <strong>Size: {selectedSize}</strong>
                  </div>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Quantity</Form.Label>
                    <div className="d-flex align-items-center">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setQty((prev) => Math.max(prev - 1, 1))}
                        disabled={qty <= 1}
                      >
                        -
                      </Button>
                      <FormControl
                        value={qty}
                        readOnly
                        className="mx-2 text-center"
                        style={{ width: "50px", userSelect: "none" }}
                      />
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() =>
                          setQty((prev) =>
                            Math.min(
                              prev + 1,
                              Math.min(
                                product.sizes.find(
                                  (sz) => sz.size === selectedSize
                                )?.quantity || 1,
                                10
                              )
                            )
                          )
                        }
                        disabled={
                          qty >=
                          Math.min(
                            product.sizes.find((sz) => sz.size === selectedSize)
                              ?.quantity || 1,
                            10
                          )
                        }
                      >
                        +
                      </Button>
                    </div>
                  </Form.Group>
                </>
              )}

              <Button
                variant="primary"
                className="mt-2 w-100"
                disabled={!selectedSize || !sizeObj || sizeObj.quantity === 0}
                onClick={() => {
                  handleBuyNowDirect();
                  setShowBuyNowPanel(false);
                }}
              >
                Buy Now
              </Button>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default ProductScreen;
